import React, { useEffect, ChangeEvent, useState, FC, MutableRefObject } from 'react';
import { PlanningBacklogMilestonesInput, CommodityMilestone, MwrStatus, BacklogMilestones } from '@bechtel/types';
import { Box, Spinner, SpinnerSize, Text, VisuallyHidden, Checkbox } from '@bechtel/gds';
import { GET_PLANNING_BACKLOG_MILESTONE_LIST } from '@bechtel/client-gql';
import { NetworkStatus, useLazyQuery } from '@apollo/client';
import { DND_IDENTIFIER, usePlanning, ErrorRetry } from '@bechtel/client-common';
import CommodityCards from '../../commodity';
import { BacklogDetailStyled, BacklogMilestoneStyled, LabelStyled } from '../styled';
import { Droppable } from 'react-beautiful-dnd';
import { filter, get, lowerCase, forEach, findIndex } from 'lodash';
import produce from 'immer';
import { MilestoneIdentifier } from '../../../types';
import { BacklogFilter } from '../types';
import { materialStatusList } from '~/detail/plan/backlog/backlog-drawer/material-status/types';
import { find } from 'lodash';
import { t, Trans } from '@lingui/macro';

interface BacklogListProps {
  setSelectedMilestoneDraggableIds: (value: string[] | []) => void;
  selectedMilestoneDraggableIds: string[];
  draggingMilestoneId: string;
  searchVal?: string;
  formData: BacklogFilter;
  milestoneIdentifiers: MutableRefObject<Map<string, MilestoneIdentifier>>;
  backlogMilestoneData: MutableRefObject<CommodityMilestone[]>;
}

export const BacklogList: FC<BacklogListProps> = ({
  setSelectedMilestoneDraggableIds,
  selectedMilestoneDraggableIds,
  draggingMilestoneId,
  searchVal,
  formData,
  milestoneIdentifiers,
  backlogMilestoneData,
}) => {
  const { isReplan, selectedWpList } = usePlanning();
  const [backlogMilestoneList, setBacklogMilestoneList] = useState<CommodityMilestone[]>([]);
  const [selected, setSelected] = useState(0);

  const [getBacklogMilestones, { loading, error, data, refetch, networkStatus, called }] = useLazyQuery<
    { backlogMilestones: BacklogMilestones },
    {
      input: PlanningBacklogMilestonesInput;
    }
  >(GET_PLANNING_BACKLOG_MILESTONE_LIST, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    //TODO: Array fields apollo keeps as different cache key for each variables. So client cache update is not possible
    // and hence go to network and fetch if the input changes
    // eg. Input : [WpId: '123'] is saved differently than Input : [WpId: '123', WpId: '213']
  });

  useEffect(() => {
    if (!selectedWpList || selectedWpList.length === 0) return;
    getBacklogMilestones({
      variables: {
        input: {
          workPackageIds: selectedWpList,
        },
      },
    });
  }, [getBacklogMilestones, selectedWpList]);

  const getFilteredBacklog = (backlogFilters: BacklogFilter, backlogMilestonesFiltered: CommodityMilestone[]) => {
    const backlogfilterData = filter(backlogMilestonesFiltered, (item: CommodityMilestone) => {
      const milestone = lowerCase(item.name);
      const commodity =
        (item.commodity.materialCommodity && lowerCase(item.commodity.materialCommodity.type.name)) ||
        (item.commodity.weld && lowerCase(item.commodity.weld.type.name));
      const material =
        item.commodity.materialCommodity && item.commodity.materialCommodity.material
          ? item.commodity.materialCommodity.material
          : null;

      const planningMaterialStatusLabel = material
        ? `${material?.mrrNumber && material?.mrrNumber?.length > 0 ? lowerCase(materialStatusList[0].label) : ''},${
            material?.mwrNumber && material?.mwrNumber?.length > 0 ? lowerCase(materialStatusList[1].label) : ''
          },${
            material.mwrs && material.mwrs.length > 0 && find(material.mwrs, (mwr) => mwr.mwrStatus == MwrStatus.LOADED)
              ? lowerCase(materialStatusList[2].label)
              : ''
          }`
        : '';

      const milestoneFilterVal = backlogFilters.milestone.map((item: string) => lowerCase(item));
      const commodityFilterVal = backlogFilters.commodityTypes.map((item: string) => lowerCase(item));
      const materialStatusFilterVal = backlogFilters.materialStatus.map((item: string) => lowerCase(item));

      return (
        (milestoneFilterVal.length > 0 ? milestoneFilterVal.some((item: string) => milestone.includes(item)) : true) &&
        (commodityFilterVal.length > 0
          ? commodityFilterVal.some((item: string) => commodity && commodity.includes(item))
          : true) &&
        (materialStatusFilterVal.length > 0
          ? materialStatusFilterVal.some(
              (item: string) => planningMaterialStatusLabel && planningMaterialStatusLabel.includes(item)
            )
          : true) &&
        (backlogFilters.expiredMilestoneFilter === true
          ? backlogFilters.expiredMilestoneFilter === item.isExpired
          : true)
      );
    });
    return backlogfilterData;
  };

  const resetBacklogSelectedMilestoneDraggableIds = () => {
    const updatedMilestoneDraggableIds = produce(selectedMilestoneDraggableIds, (draft) => {
      forEach(backlogMilestoneList, (backlogMilestone) => {
        const { dragIdentifier } = milestoneIdentifiers.current.get(backlogMilestone.id);
        const selectedIndex = findIndex(
          draft,
          (selectedMilestoneDraggableId) => selectedMilestoneDraggableId == dragIdentifier
        );
        if (selectedIndex > -1) {
          draft.splice(selectedIndex, 1);
        }
      });
      return draft;
    });
    setSelectedMilestoneDraggableIds(updatedMilestoneDraggableIds);
  };

  useEffect(() => {
    let backlogfilterData: CommodityMilestone[] = get(data, 'backlogMilestones.milestones', []);
    //Apply search
    if (searchVal && searchVal.trim() != '') {
      backlogfilterData = filter(backlogfilterData, (item: CommodityMilestone) => {
        const tag = item.commodity.materialCommodity?.tag;
        return lowerCase(tag).indexOf(lowerCase(searchVal)) !== -1;
      });
    }
    //Apply filter
    backlogfilterData = getFilteredBacklog(formData, backlogfilterData);
    //Reset selected draggables as it may go out of the results
    resetBacklogSelectedMilestoneDraggableIds();
    setBacklogMilestoneList(backlogfilterData);
  }, [data, formData, searchVal]);

  useEffect(() => {
    let plannableCount = 0,
      selected = 0;
    forEach(backlogMilestoneList, (backlogMilestone) => {
      const milestoneIdentifier = milestoneIdentifiers.current.get(backlogMilestone.id);
      if (!milestoneIdentifier) return false;
      const { dragIdentifier, canPlan } = milestoneIdentifier;
      if (canPlan) {
        plannableCount += 1;
        const selectedIndex = findIndex(
          selectedMilestoneDraggableIds,
          (selectedMilestoneDraggableId) => selectedMilestoneDraggableId == dragIdentifier
        );
        if (selectedIndex == -1) {
          if (selected > 0) return false; //for indeterminate atleast one is selected
        } else {
          selected += 1;
        }
      }
    });
    setSelected(plannableCount != 0 && selected != 0 && plannableCount == selected ? 2 : selected > 0 ? 1 : 0);
  }, [milestoneIdentifiers, selectedMilestoneDraggableIds, backlogMilestoneList]);

  const onChangeFn = (e: ChangeEvent<HTMLInputElement>) => {
    const updatedMilestoneDraggableIds = produce(selectedMilestoneDraggableIds, (draft) => {
      forEach(backlogMilestoneList, (backlogMilestone) => {
        const { dragIdentifier, canPlan } = milestoneIdentifiers.current.get(backlogMilestone.id);
        const selectedIndex = findIndex(
          draft,
          (selectedMilestoneDraggableId) => selectedMilestoneDraggableId == dragIdentifier
        );
        if (e.target.checked) {
          if (selectedIndex == -1 && canPlan) {
            const addedMilestoneDraggbleIds = draft.concat(dragIdentifier);
            draft = addedMilestoneDraggbleIds;
          }
        } else {
          if (selectedIndex > -1) {
            draft.splice(selectedIndex, 1);
          }
        }
      });
      return draft;
    });
    setSelectedMilestoneDraggableIds(updatedMilestoneDraggableIds);
  };
  useEffect(() => {
    if (backlogMilestoneData) backlogMilestoneData.current = backlogMilestoneList;
  }, [backlogMilestoneData, backlogMilestoneList]);

  return (
    <>
      {(loading || networkStatus === NetworkStatus.refetch) && (
        <BacklogDetailStyled css="height:100%;" alignItems={{ sm: 'center' }}>
          <Spinner data-testid="backlog-milestone-loading" size={SpinnerSize.MEDIUM} />
        </BacklogDetailStyled>
      )}
      {!loading && error && (
        <Box data-testid="backlog-milestone-error">
          <ErrorRetry refetch={refetch}>
            <Trans>There was error loading backlog milestones. Please click the button below to try again.</Trans>
          </ErrorRetry>
        </Box>
      )}
      {!loading && !error && called && backlogMilestoneList && backlogMilestoneList.length === 0 && (
        <BacklogDetailStyled>
          <Text data-testid="backlog-milestone-zero" style={{ textAlign: 'center' }}>
            {isReplan ? t`There are no expired milestones.` : t`There are no backlog milestones.`}
          </Text>
        </BacklogDetailStyled>
      )}

      {!loading && !error && called && backlogMilestoneList.length > 0 && (
        <Box css="overflow-y: overlay" flexDirection={{ sm: 'column' }}>
          <LabelStyled>
            <Box mr={{ sm: '400' }}>
              <Checkbox checked={selected == 2} indeterminate={selected == 1} onChange={onChangeFn} />
            </Box>
            <Text size="400" color="bechtel.grey.1000">
              <Trans>Select All Backlog</Trans>
            </Text>
          </LabelStyled>
          <Droppable droppableId={DND_IDENTIFIER.Unplanned} isDropDisabled>
            {(provided, snapshot) => {
              return (
                <BacklogMilestoneStyled ref={provided.innerRef} {...provided.droppableProps} {...snapshot}>
                  <VisuallyHidden>{provided.placeholder}</VisuallyHidden>
                  <CommodityCards
                    data-testid="commodity-card-wrapper"
                    commodityMilestones={backlogMilestoneList}
                    droppableId={DND_IDENTIFIER.Unplanned}
                    selectedMilestoneDraggableIds={selectedMilestoneDraggableIds}
                    setSelectedMilestoneDraggableIds={setSelectedMilestoneDraggableIds}
                    draggingMilestoneId={draggingMilestoneId}
                    milestoneIdentifiers={milestoneIdentifiers}
                  />
                </BacklogMilestoneStyled>
              );
            }}
          </Droppable>
        </Box>
      )}
    </>
  );
};
